import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, ArrowRight, Zap, Shield, BarChart3, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Zap, title: 'Lightning Fast', desc: 'Send emails in seconds with pre-built templates' },
    { icon: Shield, title: 'Secure & Reliable', desc: 'Enterprise-grade security with SendGrid' },
    { icon: BarChart3, title: 'Track Everything', desc: 'Monitor delivery status and engagement' },
    { icon: Users, title: 'Team Collaboration', desc: 'Role-based access for your entire team' },
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Brand & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white max-w-xl mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="px-3 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <span className="text-xl font-bold">TLA</span>
            </div>
            <span className="text-2xl font-bold tracking-tight">The Loan Alliance</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 leading-tight">
            Email Communication
            <span className="block text-white/90">Platform</span>
          </h1>
          
          <p className="text-lg text-white/80 mb-12 leading-relaxed">
            Empower your team with intelligent email automation. 
            Send personalized communications at scale with beautiful templates and real-time tracking.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors"
              >
                <feature.icon className="w-5 h-5 mb-2 text-white/90" />
                <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                <p className="text-xs text-white/70">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Trust Badge */}
          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-sm text-white/60">
              Professional email management for The Loan Alliance team
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-6">
              <div className="px-2 py-1 bg-primary/10 rounded-lg">
                <span className="text-sm font-bold text-primary">TLA</span>
              </div>
              <span className="text-xl font-bold">The Loan Alliance</span>
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">Welcome Back</CardTitle>
            <CardDescription>Sign in to access your dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
            <div className="mt-8 pt-6 border-t text-center">
              <p className="text-xs text-muted-foreground">
                Contact your administrator for account access
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
